import type { DiscoveryLocation, ParticipationMode } from '../types'

export type DiscoveryDateRange =
  | 'any'
  | 'today'
  | 'tomorrow'
  | 'week'
  | 'month'
export type DiscoveryModeFilter = 'all' | ParticipationMode

export interface DiscoveryFilters {
  savedOnly: boolean
  showPast: boolean
  friendId: string | null
  categorySlug: string
  dateRange: DiscoveryDateRange
  maxDistanceKm: number
  minSpots: number
  participationMode: DiscoveryModeFilter
  location: DiscoveryLocation | null
}

export const MIN_DISTANCE_KM = 1
export const MAX_DISTANCE_KM = 300
// The maintainer's default for the discovery filter.
export const DEFAULT_DISTANCE_KM = 50

export const DEFAULT_DISCOVERY_FILTERS: DiscoveryFilters = {
  savedOnly: false,
  showPast: false,
  friendId: null,
  categorySlug: 'all',
  dateRange: 'any',
  maxDistanceKm: DEFAULT_DISTANCE_KM,
  // Upstream always requires at least one free spot; the default is sent too.
  minSpots: 1,
  participationMode: 'all',
  location: null,
}

const DATE_RANGES: readonly DiscoveryDateRange[] = [
  'any',
  'today',
  'tomorrow',
  'week',
  'month',
]

/**
 * Maps the persisted `f472f9a` date values to the `3b39bc7` presets. The
 * rolling 7/30-day values become the equivalent calendar presets so a stored
 * filter keeps a visible chip instead of silently falling back to `any`.
 */
const LEGACY_DATE_RANGES: Record<string, DiscoveryDateRange> = {
  '7d': 'week',
  '30d': 'month',
}

const MODES: readonly DiscoveryModeFilter[] = [
  'all',
  'open',
  'approval',
  'invite',
]

function clampNumber(value: unknown, min: number, max: number, fallback: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.min(max, Math.max(min, Math.round(value)))
}

function normalizeDateRange(value: unknown): DiscoveryDateRange {
  if (typeof value !== 'string') return 'any'
  if (DATE_RANGES.includes(value as DiscoveryDateRange)) {
    return value as DiscoveryDateRange
  }
  return LEGACY_DATE_RANGES[value] ?? 'any'
}

function normalizeLocation(value: unknown): DiscoveryLocation | null {
  if (!value || typeof value !== 'object') return null
  const candidate = value as Partial<DiscoveryLocation>
  if (
    typeof candidate.latitude !== 'number' ||
    typeof candidate.longitude !== 'number' ||
    !Number.isFinite(candidate.latitude) ||
    !Number.isFinite(candidate.longitude) ||
    candidate.latitude < -90 ||
    candidate.latitude > 90 ||
    candidate.longitude < -180 ||
    candidate.longitude > 180
  ) {
    return null
  }
  return {
    city:
      typeof candidate.city === 'string' && candidate.city.trim()
        ? candidate.city.trim()
        : null,
    latitude: candidate.latitude,
    longitude: candidate.longitude,
  }
}

/** Validates persisted/user-provided filter input before it reaches the API. */
export function normalizeFilters(input: unknown): DiscoveryFilters {
  if (!input || typeof input !== 'object') return { ...DEFAULT_DISCOVERY_FILTERS }
  const value = input as Partial<DiscoveryFilters>
  return {
    savedOnly: value.savedOnly === true,
    showPast: value.showPast === true,
    friendId:
      typeof value.friendId === 'string' && value.friendId.trim()
        ? value.friendId
        : null,
    categorySlug:
      typeof value.categorySlug === 'string' && value.categorySlug.trim()
        ? value.categorySlug
        : 'all',
    dateRange: normalizeDateRange(value.dateRange),
    maxDistanceKm: clampNumber(
      value.maxDistanceKm,
      MIN_DISTANCE_KM,
      MAX_DISTANCE_KM,
      DEFAULT_DISTANCE_KM,
    ),
    minSpots: clampNumber(value.minSpots, 1, 50, 1),
    participationMode: MODES.includes(value.participationMode ?? 'all')
      ? (value.participationMode ?? 'all')
      : 'all',
    location: normalizeLocation(value.location),
  }
}

/** Number shown in the filter badge; defaults are not "active". */
export function activeFilterCount(filters: DiscoveryFilters): number {
  let count = 0
  if (filters.savedOnly) count += 1
  if (filters.showPast) count += 1
  if (filters.friendId) count += 1
  if (filters.categorySlug !== 'all') count += 1
  if (filters.dateRange !== 'any') count += 1
  if (filters.maxDistanceKm !== DEFAULT_DISTANCE_KM) count += 1
  if (filters.minSpots !== DEFAULT_DISCOVERY_FILTERS.minSpots) count += 1
  if (filters.participationMode !== 'all') count += 1
  if (filters.location) count += 1
  return count
}
